import { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { StoreContext } from '../../store';
import './index.css';

import { ReactComponent as QuickSearch } from '../../assets/quick-search.svg';
import { ReactComponent as Close } from '../../assets/close-icon.svg';
import { ReactComponent as Search } from '../../assets/search.svg';

import Show from '../../utility/Show';
import MarkerList from '../Map/marker-list';
import MustHavesGraph from '../CompareMaps/must-haves-graph';
import { CategorySearch } from '../../utility/CategorySearch';

import { debounce } from '../../services';

const MapQuickSearch = ({ showResults=true, className="", defaultInput=1, markers=[], showMarkersCount=false }) => {
  const { state, dispatch, actions } = useContext(StoreContext);
  const { originalMarkers, isEstablishmentSearchOpen, categoryFilters, listOfMarkers } = state;
  const getMarker = originalMarkers;

  const paddedCategories = useCallback(() => {
    const initial = categoryFilters.length > 0 ? [...categoryFilters] : [''];
    const missingCount = Math.max(defaultInput - initial.length, 0);
    return [...initial, ...Array(missingCount).fill('')];
  }, [categoryFilters, defaultInput]);

  const [categories, setCategories] = useState(paddedCategories());
  const [filteredMarkers, setFilteredMarkers] = useState([]);

  const filterMarkers = useCallback((updatedList) => {
    const result = (updatedList || []).flatMap((text) => {
      const search = actions.normalizeCategories(text);
      if (!search) return [];
      return (getMarker || []).filter((marker) => {
        const markerName = actions.normalizeCategories(marker.name) || '';
        const markerCategory = actions.normalizeCategories(marker.category) || 'others';
        const isSearchTextFound = markerName.includes(search) || markerCategory.includes(search);
        // Check for combined word matches (e.g., "drug store" vs "drugstore")
        const isCombinedMatch =
          actions.isCombinedWordMatch(markerCategory, search) ||
          actions.isCombinedWordMatch(markerName, search);
        return isSearchTextFound || isCombinedMatch || actions.isWholeWordMatch(markerCategory, search);
      });
    });
    const uniqueMarkers = Array.from(
      new Map(result.map((item) => [item.id, item])).values()
    );
    setFilteredMarkers(uniqueMarkers);
  }, [actions, getMarker]);

  const debouncedFilterMarkers = useMemo(
    () => debounce(filterMarkers, 200),
    [filterMarkers]
  );

  useEffect(() => {
    debouncedFilterMarkers(categories);
    // eslint-disable-next-line
  }, [originalMarkers, categories]);

  const isCategoryExists = (categories) => {
    return categories.some((item) => categoryFilters.includes(item));
  };

  const setFilters = useCallback((value, index) => {
    const updatedCategories = [...categories];
    updatedCategories[index] = value;
    setCategories(updatedCategories);
    dispatch({ type: 'UPDATE_CATEGORY', payload: { index, value } });
  }, [categories, dispatch]);

  const handleChange = (updatedList) => {
    const result = (updatedList || []).flatMap((text) => {
      const search = actions.normalizeCategories(text)
      if (!search) return []

      return (getMarker || []).filter((marker) => {
        const markerName = actions.normalizeCategories(marker.name) || ''
        const markerCategory = actions.normalizeCategories(marker.category) || 'others'
        const isSearchTextFound = markerName.includes(search) || markerCategory.includes(search)
        // Check for combined word matches (e.g., "drug store" vs "drugstore")
        const isCombinedMatch =
          actions.isCombinedWordMatch(markerCategory, search) ||
          actions.isCombinedWordMatch(markerName, search)
        return isSearchTextFound || isCombinedMatch || actions.isWholeWordMatch(markerCategory, search)
      })
    })

    const uniqueMarkers = Array.from(
      new Map(result.map((item) => [item.id, item])).values()
    )

    setFilteredMarkers(uniqueMarkers)
  }

  const handleAdd = useCallback(() => {
    setCategories((prev) => [...prev, '']);
  }, []);

  const handleClear = useCallback((index) => {
    setCategories((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const closeMapQS = useCallback(() => {
    dispatch({ type: 'SET_SHOW_MAP_QS', payload: false });
    dispatch({ type: 'RESET_CATEGORY', payload: [''] });
  }, [dispatch]);

  const getCategoryCount = useCallback((searchText) => {
    const cleanFilter = actions.normalizeCategories(searchText || '');
    return markers.filter((place) => {
      const category = actions.normalizeCategories(place.category || 'others');
      const name = actions.normalizeCategories(place.name || '');

      const isSearchTextFound =
        name.includes(cleanFilter) || category.includes(cleanFilter);

      // Check for combined word matches (e.g., "drug store" vs "drugstore")
      const isCombinedMatch =
        actions.isCombinedWordMatch(category, cleanFilter) ||
        actions.isCombinedWordMatch(name, cleanFilter);

      return (isSearchTextFound || isCombinedMatch || actions.isWholeWordMatch(category, cleanFilter));
    }).length;
  }, [markers, actions]);

  return (
    <>
      <div className={`map-qs h-auto min-h-[fit-content] ${className}`}>
        <div className='qs-header'>
          <div className="flex items-start">
            <QuickSearch />
            <div className="flex flex-col">
              Multiple Search
            </div>
          </div>
          <Show.When isTrue={!isEstablishmentSearchOpen}>
            <Close className="cursor-pointer close" onClick={closeMapQS} />
          </Show.When>
        </div>

        <div className="category-inputs w-full flex flex-col gap-4">
          {categories.map((cat, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="w-6">S{index + 1}</span>
              <CategorySearch
                key={`category-filter-${index}`}
                index={index}
                value={cat}
                placeholder="Enter an establishment"
                isClearable
                onClear={() => handleClear(index)}
                onClick={() => {}}
                onChange={(e) => {
                  const newVal = e.target?.value || '';
                  setFilters(newVal, index);
                }}
                dispatch={dispatch}
              />
              <Show.When isTrue={categories[index] !== "" && showMarkersCount}>
                <div className='text-center min-w-[34px] min-h-[34px] bg-white text-black flex items-center justify-center rounded text-sm'>
                  {getCategoryCount(cat)}
                </div>
              </Show.When>
            </div>
          ))}

          {categories.length < 5 && (
            <div className="w-full flex items-center gap-2">
              <span className="w-6 invisible">0</span>
              <button
                onClick={handleAdd}
                className="border border-white w-full rounded text-white text-xl"
              >
                +
              </button>
            </div>
          )}
        </div>
      </div>

      <Show.When
        isTrue={
          categoryFilters?.some((cat) => String(cat || '').trim() !== '') &&
          filteredMarkers.length > 0 &&
          showResults
        }
      >
        {/* <Show.When isTrue={categoryFilters?.length > 0 && categoryFilters[0] !== '' && isExploreEnabled}>
          <MustHavesGraph
            state={state}
            dispatch={dispatch}
            actions={actions}
            className='bg-[#0D0D0DD9] max-w-[430px] pt-8 rounded-lg'
            filtersOnly={true}
          />
        </Show.When> */}
        <MarkerList markers={filteredMarkers} className="max-w-[430px] bg-[#0D0D0DD9]" preserveOrder={true} />
      </Show.When>
    </>
  );
};

export default MapQuickSearch;
